<!-- src/lib/components/migration/MigrationWizard.svelte -->
<!--
  7-step migration wizard. Replaces the old MigrationPanel.
  Steps:
    1. SourcePicker      — choose Linear / Plane / Asana / Figma
    2. ConnectStep       — enter API token, validate credentials
    3. ProjectSelector   — enter project ID, trigger fetch + poll until analyzing
    4. HealthReportStep  — show health report
    5. MappingConfirm    — review / edit status mapping, start import
    6. ImportProgress    — live poll until done or error
    7. ImportResults     — final summary
-->
<script lang="ts">
  import type { MigrationSource } from '../../types.ts';
  import { fade, fly } from 'svelte/transition';

  import SourcePicker    from './SourcePicker.svelte';
  import ConnectStep     from './ConnectStep.svelte';
  import ProjectSelector from './ProjectSelector.svelte';
  import HealthReportStep from './HealthReportStep.svelte';
  import MappingConfirm  from './MappingConfirm.svelte';
  import ImportProgress  from './ImportProgress.svelte';
  import ImportResults   from './ImportResults.svelte';

  interface Props {
    workspaceId: number;
    projectId: number;
    onClose: () => void;
  }
  let { workspaceId, projectId, onClose }: Props = $props();

  // ── Wizard state ──────────────────────────────────────────────────────────
  let step        = $state(1);
  let source      = $state<MigrationSource | null>(null);
  let displayName = $state<string | null>(null);
  let runId       = $state<string | null>(null);
  let finalStatus = $state<'done' | 'error' | null>(null);
  let finalError  = $state<string | undefined>(undefined);

  // ── Step titles for the breadcrumb progress bar ───────────────────────────
  const stepLabels = ['Source', 'Connect', 'Project', 'Health', 'Mapping', 'Import', 'Done'];
  const totalSteps = stepLabels.length;

  // ── Navigation helpers ────────────────────────────────────────────────────
  function goBack() {
    if (step > 1) step--;
  }
</script>

<!-- Overlay -->
<!-- svelte-ignore a11y_click_events_have_key_events -->
<div
  class="overlay"
  role="presentation"
  transition:fade={{ duration: 120 }}
  onclick={onClose}
></div>

<!-- Modal -->
<div class="wizard" transition:fly={{ y: -20, duration: 180 }}>
  <!-- Header -->
  <header class="wizard-header">
    <span class="wizard-title">Import data</span>
    <button class="close-btn" onclick={onClose} aria-label="Close">✕</button>
  </header>

  <!-- Step progress bar -->
  <div class="progress-bar" aria-label="Wizard progress">
    {#each stepLabels as label, i}
      <div
        class="progress-step"
        class:done={i + 1 < step}
        class:active={i + 1 === step}
      >
        <span class="progress-dot">{i + 1 < step ? '✓' : i + 1}</span>
        <span class="progress-label">{label}</span>
      </div>
      {#if i < totalSteps - 1}
        <div class="progress-connector" class:done={i + 1 < step}></div>
      {/if}
    {/each}
  </div>

  <!-- Step body -->
  <div class="wizard-body">
    {#if step === 1}
      <SourcePicker
        onSelect={(src) => { source = src; step = 2; }}
        onBack={onClose}
      />
    {:else if step === 2 && source}
      <ConnectStep
        {source}
        onConnected={(name) => { displayName = name; step = 3; }}
        onBack={goBack}
      />
    {:else if step === 3 && source}
      <ProjectSelector
        {source}
        displayName={displayName ?? ''}
        {workspaceId}
        {projectId}
        onFetched={(id) => { runId = id; step = 4; }}
        onBack={goBack}
      />
    {:else if step === 4 && runId}
      <HealthReportStep
        {runId}
        onNext={() => { step = 5; }}
        onBack={goBack}
      />
    {:else if step === 5 && runId}
      <MappingConfirm
        {runId}
        onNext={() => { step = 6; }}
        onBack={goBack}
      />
    {:else if step === 6 && runId}
      <ImportProgress
        {runId}
        onComplete={(s, err) => { finalStatus = s; finalError = err; step = 7; }}
      />
    {:else if step === 7 && finalStatus}
      <ImportResults
        status={finalStatus}
        errorMsg={finalError}
        {onClose}
      />
    {:else}
      <p class="step-error">Unexpected wizard state. Please close and try again.</p>
    {/if}
  </div>
</div>

<style>
/* ── Overlay ── */
.overlay {
  position: fixed; inset: 0;
  background: rgba(0,0,0,0.6);
  z-index: 90;
}

/* ── Modal ── */
.wizard {
  position: fixed;
  top: 50%; left: 50%;
  transform: translate(-50%, -50%);
  width: 600px;
  max-width: calc(100vw - 2rem);
  max-height: calc(100vh - 4rem);
  background: var(--bg-overlay, #13141a);
  border: 1px solid rgba(255,255,255,0.08);
  border-radius: 12px;
  z-index: 91;
  display: flex;
  flex-direction: column;
  box-shadow: 0 24px 60px rgba(0,0,0,0.65);
  overflow: hidden;
}

/* ── Header ── */
.wizard-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.875rem 1.25rem;
  border-bottom: 1px solid rgba(255,255,255,0.06);
  flex-shrink: 0;
}
.wizard-title {
  font-size: 0.95rem;
  font-weight: 600;
  color: var(--text-primary);
}
.close-btn {
  background: none; border: none;
  color: var(--text-muted);
  cursor: pointer;
  font-size: 0.9rem;
  padding: 0.25rem 0.4rem;
  border-radius: 4px;
  line-height: 1;
  transition: color 100ms ease, background 100ms ease;
}
.close-btn:hover { color: var(--text-primary); background: rgba(255,255,255,0.06); }

/* ── Step progress bar ── */
.progress-bar {
  display: flex;
  align-items: center;
  padding: 0.75rem 1.25rem;
  border-bottom: 1px solid rgba(255,255,255,0.05);
  gap: 0;
  flex-shrink: 0;
  overflow-x: auto;
}

.progress-step {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.2rem;
  flex-shrink: 0;
}

.progress-dot {
  width: 22px; height: 22px;
  border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  font-size: 0.65rem;
  font-weight: 600;
  background: rgba(255,255,255,0.06);
  border: 1px solid rgba(255,255,255,0.12);
  color: var(--text-muted);
  transition: background 200ms ease, border-color 200ms ease, color 200ms ease;
}
.progress-step.active .progress-dot {
  background: oklch(0.72 0.28 310 / 0.18);
  border-color: var(--ab-purple-core);
  color: var(--ab-purple-bright);
}
.progress-step.done .progress-dot {
  background: oklch(0.72 0.28 310 / 0.1);
  border-color: oklch(0.72 0.28 310 / 0.4);
  color: var(--ab-purple-bright);
}

.progress-label {
  font-size: 0.62rem;
  color: var(--text-subtle, rgba(255,255,255,0.25));
  text-align: center;
  white-space: nowrap;
}
.progress-step.active .progress-label { color: var(--ab-purple-bright); }
.progress-step.done   .progress-label { color: var(--text-muted); }

.progress-connector {
  flex: 1;
  height: 1px;
  background: rgba(255,255,255,0.08);
  margin: 0 0.3rem;
  margin-bottom: 0.9rem; /* align with dots not labels */
  transition: background 200ms ease;
}
.progress-connector.done { background: oklch(0.72 0.28 310 / 0.35); }

/* ── Step body ── */
.wizard-body {
  flex: 1;
  overflow-y: auto;
  padding: 1.25rem;
  min-height: 0;
}

.step-error {
  font-size: 0.82rem;
  color: var(--ab-red-bright);
  text-align: center;
  padding: 2rem 0;
}
</style>
